import { useEffect, useRef, useCallback, useState } from 'react';
import {
    learningSessionService,
    LearningModeType,
    DevicePlatform,
    EndSessionReason,
} from '../services/learningSessionService';

/**
 * Hook options
 */
interface UseLearningSessionOptions {
    /** Course ID to track */
    courseId: string;
    /** Learning mode being used */
    mode: LearningModeType;
    /** Whether tracking is enabled (default: true) */
    enabled?: boolean;
    /** Platform (default: 'web') */
    platform?: DevicePlatform;
    /** Initial context data */
    contextData?: {
        sentenceIndex?: number;
        vocabularyWordId?: string;
        noteId?: string;
        quizQuestionIndex?: number;
    };
    /** Heartbeat interval in ms (default: 30000) */
    heartbeatInterval?: number;
    /** Callback when session ends */
    onSessionEnd?: (durationSeconds: number) => void;
}

/**
 * Hook return value
 */
interface UseLearningSessionReturn {
    /** Current session ID (null if not active) */
    sessionId: string | null;
    /** Current session duration in seconds */
    durationSeconds: number;
    /** Whether session is currently active */
    isActive: boolean;
    /** Whether session is loading/starting */
    isLoading: boolean;
    /** Error message if any */
    error: string | null;
    /** Manually end the session */
    endSession: (reason?: EndSessionReason) => Promise<void>;
    /** Update context data */
    updateContext: (contextData: Record<string, unknown>) => void;
}

// Heartbeat interval: 30 seconds
const DEFAULT_HEARTBEAT_INTERVAL = 30000;

/**
 * Hook for managing learning session lifecycle
 *
 * Automatically:
 * - Starts a session when mounted (if enabled)
 * - Sends heartbeats every 30 seconds
 * - Handles page visibility changes (pauses when hidden)
 * - Ends session on unmount or page unload
 *
 * @example
 * ```tsx
 * const { sessionId, durationSeconds, isActive, endSession } = useLearningSession({
 *   courseId: 'xxx',
 *   mode: 'dictation',
 *   enabled: true,
 * });
 * ```
 */
export function useLearningSession({
    courseId,
    mode,
    enabled = true,
    platform = 'web',
    contextData,
    heartbeatInterval = DEFAULT_HEARTBEAT_INTERVAL,
    onSessionEnd,
}: UseLearningSessionOptions): UseLearningSessionReturn {
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [durationSeconds, setDurationSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Refs for cleanup and state management
    const sessionIdRef = useRef<string | null>(null);
    const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
    const contextDataRef = useRef(contextData);
    const isEndingRef = useRef(false);
    const isMountedRef = useRef(true);

    // Update context ref when it changes
    useEffect(() => {
        contextDataRef.current = contextData;
    }, [contextData]);

    /**
     * Stop heartbeat timer
     */
    const stopHeartbeat = useCallback(() => {
        if (heartbeatTimerRef.current) {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        }
    }, []);

    /**
     * End the session
     */
    const endSession = useCallback(async (reason: EndSessionReason = 'manual') => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId || isEndingRef.current) return;

        isEndingRef.current = true;
        stopHeartbeat();

        try {
            const result = await learningSessionService.endSession(currentSessionId, { reason });
            if (isMountedRef.current) {
                setSessionId(null);
                setDurationSeconds(result.durationSeconds);
                onSessionEnd?.(result.durationSeconds);
            }
            sessionIdRef.current = null;
        } catch (err) {
            console.error('Failed to end learning session:', err);
            // Still clear session on error to prevent stuck state
            sessionIdRef.current = null;
            if (isMountedRef.current) {
                setSessionId(null);
            }
        } finally {
            isEndingRef.current = false;
        }
    }, [stopHeartbeat, onSessionEnd]);

    /**
     * Send heartbeat
     */
    const sendHeartbeat = useCallback(async () => {
        const currentSessionId = sessionIdRef.current;
        if (!currentSessionId) return;

        try {
            const result = await learningSessionService.heartbeat(currentSessionId, {
                contextData: contextDataRef.current,
            });
            if (isMountedRef.current) {
                setDurationSeconds(result.durationSeconds);
            }
        } catch (err) {
            console.error('Heartbeat failed:', err);
            // If heartbeat fails, the session might have been ended/abandoned
            // We could try to reconnect or notify the user
        }
    }, []);

    /**
     * Start heartbeat timer
     */
    const startHeartbeat = useCallback(() => {
        stopHeartbeat();
        heartbeatTimerRef.current = setInterval(sendHeartbeat, heartbeatInterval);
    }, [stopHeartbeat, sendHeartbeat, heartbeatInterval]);

    /**
     * Start a new session
     */
    const startSession = useCallback(async () => {
        if (sessionIdRef.current || isLoading) return;

        setIsLoading(true);
        setError(null);

        try {
            const result = await learningSessionService.startSession({
                courseId,
                mode,
                platform,
                contextData,
            });

            sessionIdRef.current = result.sessionId;
            if (isMountedRef.current) {
                setSessionId(result.sessionId);
                setDurationSeconds(0);
                startHeartbeat();
            }
        } catch (err: any) {
            console.error('Failed to start learning session:', err);
            if (isMountedRef.current) {
                setError(err?.message || 'Failed to start session');
            }
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [courseId, mode, platform, contextData, isLoading, startHeartbeat]);

    /**
     * Update context data for next heartbeat
     */
    const updateContext = useCallback((newContextData: Record<string, unknown>) => {
        contextDataRef.current = { ...contextDataRef.current, ...newContextData };
    }, []);

    /**
     * Handle visibility change (pause/resume)
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden - stop heartbeat but don't end session
                stopHeartbeat();
            } else {
                // Page is visible again - restart heartbeat and send one immediately
                if (sessionIdRef.current) {
                    sendHeartbeat();
                    startHeartbeat();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [stopHeartbeat, startHeartbeat, sendHeartbeat]);

    /**
     * Handle page unload - use sendBeacon for reliable delivery
     */
    useEffect(() => {
        const handleBeforeUnload = () => {
            const currentSessionId = sessionIdRef.current;
            if (currentSessionId) {
                // Use sendBeacon for reliable delivery during page unload
                learningSessionService.endSessionBeacon(currentSessionId, 'page_unload');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    /**
     * Start session on mount, end on unmount
     */
    useEffect(() => {
        isMountedRef.current = true;

        if (enabled && courseId && mode) {
            startSession();
        }

        return () => {
            isMountedRef.current = false;
            stopHeartbeat();

            // End session on unmount (e.g., navigating away)
            const currentSessionId = sessionIdRef.current;
            if (currentSessionId) {
                // Fire and forget - we're unmounting
                learningSessionService.endSession(currentSessionId, { reason: 'navigation' })
                    .catch(console.error);
                sessionIdRef.current = null;
            }
        };
    }, [enabled, courseId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        sessionId,
        durationSeconds,
        isActive: !!sessionId,
        isLoading,
        error,
        endSession,
        updateContext,
    };
}
